'use client';

import { useMultidietSetup } from '@/features/multidiet-picker/api/hooks/use-multidiet-setup';
import useMultidietSetupStore from '@/features/multidiet-picker/store/multidiet-picker.store';
import { MultidietPickerFormType } from '@/features/multidiet-picker/types/multidiet-picker-form.type';
import useMultidietStore from '@/features/multidiet/store/multidiet-store';
import { MultidietParamsType } from '@/features/multidiet/types/multidiet.type';
import { getLocaleForPathname, getPathname } from '@/i18n/navigation';
import { useMobile } from '@/lib/hooks/use-mobile';
import { objectToQueryString } from '@/lib/utilities';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const multidietPickerFormSchema = z.object({
  dietTypeMobile: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
    }),
  ),
  dietTypeAny: z.string(),
  dietType: z.array(z.string()),
  calories: z.string(),
});

export const useMultidietPickerForm = () => {
  const t = useTranslations();
  const router = useRouter();
  const locale = getLocaleForPathname();
  const currentPathname = usePathname();
  const pathname = getPathname({ href: '/multidiet-configuration', locale });
  const searchParams = useSearchParams();
  const [previousDietTypeMobile, setPreviousDietTypeMobile] = useState<
    { label: string; value: string }[]
  >([]);
  const {
    setIsFetching,
    setMultidietSetup,
    setMultidietSetupLabels,
    setFirstDeliveryDate,
    setMultidietSelectedLabel,
    multidietSetup,
  } = useMultidietSetupStore();
  const { deliveryDate } = useMultidietStore();

  const { isMobileExtendedPlus } = useMobile();

  const form = useForm<MultidietPickerFormType>({
    resolver: zodResolver(multidietPickerFormSchema),
    defaultValues: {
      dietTypeMobile: [],
      dietTypeAny: searchParams.get('tags') ? '' : 'ANY',
      dietType: searchParams.get('tags')
        ? searchParams.get('tags')?.split(',')
        : [],
      calories: searchParams.get('calories') || '1800',
    },
  });

  const {
    data: multidietSetupResponse,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
  } = useMultidietSetup({
    queryParams: {
      tags: isMobileExtendedPlus
        ? form
            .getValues('dietTypeMobile')
            .map((type) => type.value)
            .join(',')
        : form.getValues('dietType').join(','),
      calories: form.getValues('calories'),
    },
    options: {
      staleTime: 0,
    },
  });

  const dietTypeWatcher = useWatch({
    control: form.control,
    name: 'dietType',
  });

  const dietTypeMobileWatcher = useWatch({
    control: form.control,
    name: 'dietTypeMobile',
  });

  const caloriesWatcher = useWatch({
    control: form.control,
    name: 'calories',
  });

  const dietTypeAnyWatcher = useWatch({
    control: form.control,
    name: 'dietTypeAny',
  });

  useEffect(() => {
    if (isMobileExtendedPlus) {
      const anyType = [{ label: t('dietTags.ANY'), value: 'ANY' }];
      const dietTypeMobile = searchParams.get('tags')
        ? searchParams
            .get('tags')
            ?.split(',')
            .map((tag) => ({
              label: t(`dietTags.${tag}`),
              value: tag,
            }))
        : anyType;

      form.setValue('dietTypeMobile', dietTypeMobile || anyType);
    }
  }, []);

  useEffect(() => {
    if (!isMobileExtendedPlus) {
      if (dietTypeAnyWatcher === 'ANY' && dietTypeWatcher.length !== 0) {
        form.setValue('dietTypeAny', '');
      }

      if (dietTypeAnyWatcher !== 'ANY' && dietTypeWatcher.length === 0) {
        form.setValue('dietTypeAny', 'ANY');
      }

      if (!(dietTypeWatcher.length === 0 && dietTypeAnyWatcher === 'ANY')) {
        refetch();
      }

      // decision to allow only one diet type to be selected at the start, not changing the form's operating principle
      if (dietTypeWatcher.length > 1) {
        form.setValue('dietType', dietTypeWatcher.slice(1));
      }
    }
  }, [dietTypeWatcher, dietTypeAnyWatcher, isMobileExtendedPlus]);

  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (isSuccess) {
      setMultidietSetup(multidietSetupResponse.setup);
      setMultidietSetupLabels(multidietSetupResponse.labels);
      setFirstDeliveryDate(new Date(multidietSetupResponse.firstDeliveryDate));
    }
  }, [isSuccess, multidietSetupResponse]);

  const hasAnySelected = (diets: { value: string }[]) =>
    diets.some((type) => type.value === 'ANY');

  const firstNonAnyDiet = multidietSetup?.dietTypes.find(
    (type) => type.dietTag !== 'ANY',
  );

  useEffect(() => {
    if (hasAnySelected(previousDietTypeMobile)) {
      if (dietTypeMobileWatcher.length === 0) {
        form.setValue('dietTypeMobile', [
          {
            label: t(`dietTags.${firstNonAnyDiet?.dietTag}`),
            value: firstNonAnyDiet?.dietTag || '',
          },
        ]);
        setPreviousDietTypeMobile(dietTypeMobileWatcher);
        return;
      }
      if (dietTypeMobileWatcher.length > 1) {
        form.setValue(
          'dietTypeMobile',
          dietTypeMobileWatcher.filter((type) => type.value !== 'ANY'),
        );
        setPreviousDietTypeMobile(dietTypeMobileWatcher);
        return;
      }
    }

    if (hasAnySelected(dietTypeMobileWatcher)) {
      if (
        !hasAnySelected(previousDietTypeMobile) &&
        previousDietTypeMobile.length > 0
      ) {
        form.setValue('dietTypeMobile', [
          {
            label: t(`dietTags.ANY`),
            value: 'ANY',
          },
        ]);
        setPreviousDietTypeMobile(dietTypeMobileWatcher);
        return;
      }
    }

    if (
      previousDietTypeMobile.length > 0 &&
      dietTypeMobileWatcher.length === 0
    ) {
      form.setValue('dietTypeMobile', [
        {
          label: t(`dietTags.ANY`),
          value: 'ANY',
        },
      ]);
      setPreviousDietTypeMobile(dietTypeMobileWatcher);
      return;
    }

    // decision to allow only one diet type to be selected at the start, not changing the form's operating principle
    if (dietTypeMobileWatcher.length > 1) {
      form.setValue('dietTypeMobile', dietTypeMobileWatcher.slice(1));
    }

    refetch();

    setPreviousDietTypeMobile(dietTypeMobileWatcher);
  }, [dietTypeMobileWatcher]);

  const onSubmit = (
    data: {
      dietType: string[];
      dietTypeMobile: { label: string; value: string }[];
      dietTypeAny: string;
      calories: string;
    },
    event?: React.BaseSyntheticEvent,
  ) => {
    event?.preventDefault();

    const nativeEvent = event?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
    const variantJson = submitter?.dataset?.dietvariant;
    const variant = variantJson ? JSON.parse(variantJson) : null;
    setMultidietSelectedLabel(variant);
    const parsedData: MultidietParamsType = {
      tags: data.dietType.join(','),
      calories: Number(data.calories),
      type: variant?.type || 'BASIC',
    };

    if (data.dietTypeMobile.length !== 0) {
      parsedData.tags = data.dietTypeMobile.map((type) => type.value).join(',');
    }

    if (
      data.dietType.length === 0 &&
      data.dietTypeMobile.some((type) => type.value === 'ANY')
    ) {
      delete parsedData.tags;
    }

    if (deliveryDate && currentPathname.includes(pathname)) {
      parsedData.dates = deliveryDate
        .map((date) => format(date, 'yyyy-MM-dd'))
        .join(',');
    }
    setIsFetching(false);

    window.location.href = `${pathname}${objectToQueryString(parsedData)}`;
  };

  return { isLoading, form, dietTypeWatcher, caloriesWatcher, onSubmit };
};
